import { Flex, Heading, ListItem, OrderedList, Stack, Text, Button, FormControl, IconButton } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Section } from "./_Section";
import { SellerData } from "../../models/Sellers";
import { ModalCreditCard } from "./CreditCard";
import { ModalActiveBanking } from "./ModalActiveBanking";
import { ModalCreateBankingAccount } from "./ModalCreateBankingAccount";
import { ModalExcelCreditCard } from "./ModalExcelCreditCard";
import { formatCurrency, getSwapBackgroundColor, getSwapColor, getSwapStatus, getSplitBackgroundColor, getSplitColor, getSplitStatus, getSplitStatusButtonText, showToast } from "../../utils";
import { copyTextToClipboard } from "../../utils/copyToClipboard";
import { CopyIcon } from "../../styles/icons/CopyIcon";
import { Input } from "../../components/Forms/Input";
import { SelectButton } from "../../components/Forms/SelectButton";
import { Tooltip } from "../../components/Tooltip";
import { useSplit } from "../../hooks/useSlipt";
import { PolicyInterval, Split } from "../../models/Split";

interface LoginSectionProps {
    seller: SellerData;
}

export function BankingSection({ seller }: LoginSectionProps) {
    let { id } = useParams();
    const user = JSON.parse(localStorage.getItem("barte-user-info"));
    const [editSection, setEditSection] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const { register, handleSubmit, watch } = useForm();
    const transferPolicyIntervalValue = watch("transferPolicyInterval");

    const {
        editSellerSplitConfig,
        getSliptInitialData,
        setSplitModalState,
        sellerSplitData,
        splitSwitchState,
        splitModalState,
        getSwapInitialData,
        sellerSwapData,
        closeSwapModalState,
        setCloseSwapModalState,
        generateSwapPixCredit,
    } = useSplit();

    const formatFormSwapType = (type: string) => {
        const formattedType = {
            DEBIT: "debit",
            CREDIT: "credit",
            INVESTMENT: "investment"
        }

        return formattedType[type] || type.toLocaleLowerCase();
    }

    const formatSwapTypeName = (name: string) => {
        const formattedName = {
            DEBIT: "Débito",
            CREDIT: "Crédito",
            INVESTMENT: "Investimento"
        }
        return formattedName[name] || name;
    }

    const onSubmit = handleSubmit(async (data) => {
        if (editSection === "swap") {
            try {
                const splitData: Split = {
                    mcc: data.mcc,
                    transferPolicyDay: data.transferPolicyInterval === "DAILY" ? 0 : data.transferPolicyDay,
                    transferPolicyEnabled: splitSwitchState ?? false,
                    transferPolicyInterval: data.transferPolicyInterval as PolicyInterval
                };
                await editSellerSplitConfig(id, splitData);
                setEditSection("");
                showToast({
                    message: "Informações atualizadas com sucesso",
                    type: "success",
                });
            } catch (error: any) {
                setEditSection("");
                error?.response?.data?.errors?.map((error: any) =>
                    showToast({
                        type: "error",
                        message: `${error?.code} - ${error?.description}`,
                    })
                );
            }
        }
    });

    function handleOpenSplitModal() {
        setSplitModalState(!splitModalState);
    }

    function handleOpenCloseSwapModal() {
        setCloseSwapModalState(!closeSwapModalState);
    }

    async function handleActivatePix(accountId: string) {
        try {
            setIsLoading(true);
            await generateSwapPixCredit(id, accountId);
        } catch (error: any) {
            error?.response?.data?.errors?.map((error: any) => (
                showToast({
                    type: "error",
                    message: `${error?.code} - ${error?.description}`
                })
            ));
        } finally {
            setIsLoading(false);
            getSwapInitialData(id);
        }
    }

    useEffect(() => {
        getSliptInitialData(id);
        getSwapInitialData(id);
        // eslint-disable-next-line
    }, [id]);

    return (
        <>
            <Section
                sectionTitle="Telas de Banking"
                onClickEditButton={() => { }}
                onClickConfirmButton={() => { }}
                buttons={[]}
                isEditButtonHidden
            >
                <Flex
                    w="100%"
                    gap="4rem"
                    align="center"
                >
                    <Stack
                        spacing="20px"
                    >
                        <Flex width="100%" maxWidth="280px" gap="7px" align="center" justify="space-between">
                            <Text
                                fontSize="12px"
                                fontWeight="400"
                                lineHeight="16px"
                                fontFamily="Hero New"
                                color="rgba(138, 148, 168, 1)"
                            >
                                Tela Ativar Conta PJ (KYC)
                            </Text>
                            <ModalCreateBankingAccount />
                        </Flex>
                        <Flex width="100%" maxWidth="280px" gap="7px" align="center" justify="space-between">
                            <Text
                                fontSize="12px"
                                fontWeight="400"
                                lineHeight="16px"
                                fontFamily="Hero New"
                                color="rgba(138, 148, 168, 1)"
                            >
                                Tela de cartão de crédito
                            </Text>
                            <ModalCreditCard />
                        </Flex>
                        <Flex width="100%" maxWidth="280px" gap="7px" align="center" justify="space-between">
                            <Text
                                fontSize="12px"
                                fontWeight="400"
                                lineHeight="16px"
                                fontFamily="Hero New"
                                color="rgba(138, 148, 168, 1)"
                            >
                                Ativar banking
                            </Text>
                            <ModalActiveBanking
                                activeBanking={seller?.bankingType === "BARTE_BANKING"}
                            />
                        </Flex>
                    </Stack>
                    <Flex
                        flexDirection="column"
                        gap="7px"
                        bgColor="yellow.200"
                        borderRadius="8px"
                        maxWidth="380px"
                        align="center"
                        justify="center"
                        width="100%"
                        p="10px"
                        opacity={0.6}
                    >
                        <Heading
                            fontSize="14px"
                            fontWeight="600"
                            lineHeight="16px"
                            fontFamily="Hero New"
                            color="rgba(0, 0, 0, 0.85)"
                        >
                            Atenção! Siga o Passo a Passo para ativar a conta PJ, para não haver problemas.
                        </Heading>
                        <Text
                            fontSize="12px"
                            fontWeight="400"
                            lineHeight="16px"
                            fontFamily="Hero New"
                            color="rgba(138, 148, 168, 1)"
                        >
                            <OrderedList spacing={1} margin={0} paddingLeft="20px">
                                <ListItem>Ative o toggle de ativar conta PJ (KYC)</ListItem>
                                <ListItem>Peça para o seller relogar no sistema</ListItem>
                                <ListItem>O seller deve acessar a tela de criação de conta de banking</ListItem>
                                <ListItem>Com o kyc aprovado, ativar o toggle (Tela de cartão de crédito).</ListItem>
                                <ListItem>Pedir para o seller relogar no sistema.</ListItem>
                                <ListItem>Antes de ativar o toggle de Barte Banking, comunicar ao time de financeiro.</ListItem>
                            </OrderedList>
                        </Text>
                    </Flex>
                </Flex>
            </Section>
            <Section
                sectionTitle="Relatórios"
                onClickEditButton={() => { }}
                onClickConfirmButton={() => { }}
                buttons={[]}
                isEditButtonHidden
            >
                <Flex width="100%" maxWidth="280px" gap="7px" align="center" justify="space-between">
                    <ModalExcelCreditCard />
                </Flex>
            </Section>
            {user?.email?.includes("@barte") && (
                <Section
                    sectionTitle="Conta Swap"
                    isEdit={editSection === "swap"}
                    onClickEditButton={() => {
                        setEditSection("swap");
                    }}
                    onClickConfirmButton={onSubmit}
                    buttons={[
                        <Button
                            variant="cancel"
                            isDisabled={sellerSwapData[0]?.accountStatus !== "ACTIVE"}
                            onClick={handleOpenCloseSwapModal}
                        >
                            Encerrar conta
                        </Button>
                    ]}
                >
                    {sellerSwapData && sellerSwapData.map(sellerSwapData => (
                        <>
                            <Flex
                                gap="2"
                                align="center"
                            >
                                <Heading
                                    fontSize="1rem"
                                >
                                    Conta de{formatSwapTypeName(sellerSwapData?.type)}
                                </Heading>
                                {sellerSwapData?.accountStatus && (
                                    <Text
                                        p="0 4px"
                                        backgroundColor={getSwapBackgroundColor(sellerSwapData?.accountStatus)}
                                        color={getSwapColor(sellerSwapData?.accountStatus)}
                                    >
                                        {getSwapStatus(sellerSwapData?.accountStatus)}
                                    </Text>
                                )}

                            </Flex>
                            <Flex gap={4}>
                                <FormControl maxWidth="280px">
                                    <Input
                                        label={`Saldo de ${formatSwapTypeName(sellerSwapData?.type)}`}
                                        defaultValue={formatCurrency({ value: sellerSwapData?.balance })}
                                        name={`${formatFormSwapType(sellerSwapData?.type)}Balance`}
                                        p="0"
                                        register={{ ...register(`${formatFormSwapType(sellerSwapData?.type)}Balance`) }}
                                        mask="currency"
                                        _disabled={{
                                            bg: "transparent",
                                            border: "none",
                                        }}
                                        isDisabled
                                    />
                                </FormControl>
                                <Flex
                                    gap="4"
                                    align="center"
                                >
                                    <FormControl
                                        maxW="360px"
                                        w="100%"
                                    >
                                        <Flex
                                            maxW="100%"
                                            gap="4"
                                            align="center"
                                        >
                                            <Input
                                                defaultValue={sellerSwapData?.accountKey}
                                                label={`Pix ${formatSwapTypeName(sellerSwapData?.type)}`}
                                                name={`${formatFormSwapType(sellerSwapData?.type)}AccountKey`}
                                                p="0"
                                                register={{ ...register(`${formatFormSwapType(sellerSwapData?.type)}AccountKey`) }}
                                                _disabled={{
                                                    bg: "transparent",
                                                    border: "none",
                                                }}
                                                isDisabled
                                                type="text"
                                                w="100%"
                                                _focus={{
                                                    border: "none",
                                                }}
                                                _hover={{
                                                    cursor: "text",
                                                }}
                                            />
                                            <IconButton
                                                aria-label="copy to clipboard"
                                                bgColor="transparent"
                                                icon={<CopyIcon />}
                                                type="button"
                                                onClick={async () => {
                                                    if (sellerSwapData?.accountKey) {
                                                        await copyTextToClipboard(sellerSwapData?.accountKey);
                                                    }
                                                }}
                                                _hover={{
                                                    cursor: "pointer",
                                                }}
                                            />
                                        </Flex>
                                    </FormControl>
                                    {sellerSwapData?.accountStatus && sellerSwapData?.type === "CREDIT" && (
                                        <Button
                                            colorScheme="pink"
                                            isLoading={isLoading}
                                            isDisabled={isLoading}
                                            onClick={() => handleActivatePix(sellerSwapData?.accountId)}
                                            w="320px"
                                            size="sm"
                                        >
                                            Gerar pix crédito
                                        </Button>
                                    )}
                                </Flex>
                            </Flex>
                            <Flex
                                gap={4}
                            >
                                <FormControl maxWidth="280px">
                                    <Input
                                        label="Banco"
                                        name={`${formatFormSwapType(sellerSwapData?.type)}BankIspb`}
                                        defaultValue={sellerSwapData?.bankingAccount?.bankIspb}
                                        p="0"
                                        register={{ ...register(`${formatFormSwapType(sellerSwapData?.type)}BankIspb`) }}
                                        _disabled={{
                                            bg: "transparent",
                                            border: "none",
                                        }}
                                        isDisabled
                                    />
                                </FormControl>
                                <FormControl
                                    maxWidth="280px"
                                    display="flex"
                                    alignItems="center"
                                    gap="2"
                                    position="relative"
                                >
                                    <Input
                                        p="0"
                                        label="Agencia"
                                        name={`${formatFormSwapType(sellerSwapData?.type)}Branch`}
                                        defaultValue={sellerSwapData?.bankingAccount?.branch}
                                        register={{ ...register(`${formatFormSwapType(sellerSwapData?.type)}Branch`) }}
                                        _disabled={{
                                            bg: "transparent",
                                            border: "none",
                                        }}
                                        isDisabled
                                    />
                                </FormControl>
                                <FormControl
                                    maxWidth="280px"
                                    display="flex"
                                    alignItems="center"
                                    gap="2"
                                >
                                    <Input
                                        p="0"
                                        label="Conta"
                                        name={`${formatFormSwapType(sellerSwapData?.type)}AccountNumber`}
                                        defaultValue={sellerSwapData?.bankingAccount?.accountNumber}
                                        register={{ ...register(`${formatFormSwapType(sellerSwapData?.type)}AccountNumber`) }}
                                        _disabled={{
                                            bg: "transparent",
                                            border: "none",
                                        }}
                                        isDisabled
                                    />
                                </FormControl>
                                <FormControl
                                    maxWidth="280px"
                                    marginTop="20px"
                                >
                                    <SelectButton
                                        name={`${formatFormSwapType(sellerSwapData?.type)}AccountType`}
                                        disabled={editSection !== "swap"}
                                        label="Tipo de conta"
                                        variant="ghost"
                                        bgColor="#EDF1F7"
                                        h="27px"
                                        defaultValue={sellerSwapData?.bankingAccount?.accountType}
                                        register={{ ...register(`${formatFormSwapType(sellerSwapData?.type)}AccountType`) }}
                                        isDisabled
                                    >
                                        <option value="CONTA_CORRENTE">Conta corrente</option>
                                        <option value="POUPANCA">Conta poupança</option>
                                    </SelectButton>
                                </FormControl>
                            </Flex>
                        </>
                    ))}
                </Section>
            )}
            {user?.email?.includes("@barte") && (
                <Section
                    sectionTitle="Conta para split"
                    isEdit={editSection === "split"}
                    onClickEditButton={() => {
                        setEditSection("split");
                    }}
                    onClickConfirmButton={() => { }}
                >
                    <Flex gap={4}>
                        <Flex
                            flexDir="column"
                            maxWidth="280px"
                            w="100%"
                            alignItems="flex-start"
                            gap="4px"
                        >
                            <Text
                                color="#8A94A8"
                                fontWeight="400"
                                fontSize="12px"
                                lineHeight="16px"
                            >
                                Status de conta
                            </Text>
                            <Tooltip
                                text={sellerSplitData?.errorReason}
                            >
                                <Text
                                    p="0 4px"
                                    backgroundColor={getSplitBackgroundColor(sellerSplitData?.splitStatus)}
                                    color={getSplitColor(sellerSplitData?.splitStatus)}
                                >
                                    {getSplitStatus(sellerSplitData?.splitStatus)}
                                </Text>
                            </Tooltip>

                            {(sellerSplitData?.splitStatus === "INACTIVE" ||
                                sellerSplitData?.splitStatus === "FAILED") && (
                                    <Button
                                        variant="ghost"
                                        p="0"
                                        m="0"
                                        maxH="16px"
                                        textDecor="underline"
                                        fontSize="12px"
                                        color="gray.500"
                                        textDecorationColor="gray.600"
                                        fontWeight="400"
                                        _hover={{
                                            color: "gray.700"
                                        }}
                                        onClick={handleOpenSplitModal}
                                    >
                                        {getSplitStatusButtonText(sellerSplitData?.splitStatus)}
                                    </Button>
                                )}
                        </Flex>
                        <FormControl maxWidth="280px">
                            <Input
                                label="MCC"
                                name="mcc"
                                defaultValue={sellerSplitData?.mcc}
                                p="0"
                                register={{ ...register("mcc") }}
                                _disabled={{
                                    bg: "transparent",
                                    border: "none",
                                }}
                                isDisabled={editSection !== "split"}
                            />
                        </FormControl>
                    </Flex>
                    <Flex gap={4} align="center">
                        {/* <FormControl maxWidth="280px">
              <Flex marginTop="2rem" align="center" gap="2rem">
                <Text>Repasse automático</Text>
                <Switch
                  defaultChecked={splitSwitchState}
                  isChecked={splitSwitchState}
                  onChange={() => setSplitSwitchState(!splitSwitchState)}
                  disabled={editSection !== "split"}
                />
              </Flex>
            </FormControl> */}
                        <FormControl maxWidth="280px">
                            <SelectButton
                                defaultValue={sellerSplitData?.transferPolicyInterval}
                                name="transferPolicyInterval"
                                disabled={editSection !== "split"}
                                label="Frequência do repasse"
                                variant="ghost"
                                bgColor="#EDF1F7"
                                h="27px"
                                register={{ ...register("transferPolicyInterval") }}
                            >
                                <option value="DAILY">Diária</option>
                                <option value="WEEKLY">Semanal</option>
                                <option value="MONTHLY">Mensal</option>
                            </SelectButton>
                        </FormControl>
                        <FormControl maxWidth="280px">
                            {transferPolicyIntervalValue === "WEEKLY" && (
                                <SelectButton
                                    name="transferPolicyDay"
                                    disabled={editSection !== "split" || transferPolicyIntervalValue === "DAILY"}
                                    label="Dia do repasse"
                                    variant="ghost"
                                    bgColor="#EDF1F7"
                                    h="27px"
                                    register={{ ...register("transferPolicyDay") }}
                                >
                                    <option value="1">Segunda-feira</option>
                                    <option value="2">Terça-feira</option>
                                    <option value="3">Quarta-feira</option>
                                    <option value="4">Quinta-feira</option>
                                    <option value="5">Sexta-feira</option>
                                </SelectButton>
                            )}
                            {transferPolicyIntervalValue === "MONTHLY" && (
                                <SelectButton
                                    name="transferPolicyDay"
                                    disabled={editSection !== "split" || transferPolicyIntervalValue === "DAILY"}
                                    label="Dia do repasse"
                                    variant="ghost"
                                    bgColor="#EDF1F7"
                                    h="27px"
                                    register={{ ...register("transferPolicyDay") }}
                                >
                                    {Array.from({ length: 30 }, (_, index) => (
                                        <option key={index + 1} value={index + 1}>{index + 1}</option>
                                    ))}
                                </SelectButton>
                            )}
                        </FormControl>
                    </Flex>
                </Section>
            )}
        </>
    );
}